import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Box, Typography, Divider, Avatar, Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HistoryIcon from '@mui/icons-material/History';
import BookIcon from '@mui/icons-material/Book';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import { useAuth } from '../../hooks/useAuth';

const DRAWER_WIDTH = 240;

const adminNav = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard' },
  { label: 'Books', icon: <LibraryBooksIcon />, path: '/admin/books' },
  { label: 'Books Requests', icon: <RequestPageIcon />, path: '/admin/book-requests' },
  { label: 'Publishers', icon: <BusinessIcon />, path: '/admin/publishers' },
  { label: 'Publisher Content', icon: <FolderOpenIcon />, path: '/admin/publisher-content' },
  { label: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
  { label: 'Admin Upload', icon: <CloudUploadIcon />, path: '/admin/upload' },
  { label: 'Upload History', icon: <HistoryIcon />, path: '/admin/upload-history' },
];

const publisherNav = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/publisher/dashboard' },
  { label: 'My Books', icon: <LibraryBooksIcon />, path: '/publisher/books' },
  { label: 'Upload Content', icon: <CloudUploadIcon />, path: '/publisher/upload' },
  { label: 'My Submissions', icon: <FolderOpenIcon />, path: '/publisher/submissions' },
  { label: 'Agreement', icon: <AssignmentIcon />, path: '/publisher/agreement' },
];

const userNav = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Browse Books', icon: <LibraryBooksIcon />, path: '/books' },
];

function getNav(role) {
  if (role === 'admin') return adminNav;
  if (role === 'publisher') return publisherNav;
  return userNav;
}

export default function Sidebar({ open, onClose, variant = 'permanent' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const navItems = getNav(user?.role);

  const roleColors = { admin: 'error', publisher: 'primary', end_user: 'success' };
  const roleLabel = { admin: 'Admin', publisher: 'Publisher', end_user: 'User' };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: 'primary.main', minHeight: '64px !important' }}>
        <BookIcon sx={{ color: 'white', mr: 1 }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
          Book Portal
        </Typography>
      </Toolbar>

      <Box sx={{ px: 2, py: 2, bgcolor: 'primary.dark' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
            {user?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
              {user?.name}
            </Typography>
            <Chip
              label={roleLabel[user?.role]}
              size="small"
              color={roleColors[user?.role] || 'default'}
              sx={{ height: 18, fontSize: 10, mt: 0.3 }}
            />
          </Box>
        </Box>
      </Box>

      <Divider />

      <List sx={{ flex: 1, pt: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path || location.pathname.startsWith(item.path + '/')}
              onClick={() => { navigate(item.path); if (onClose) onClose(); }}
              sx={{
                mx: 1, borderRadius: 2, mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': { color: 'white' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.disabled">
          Book Publishing Portal v1.0
        </Typography>
      </Box>
    </Box>
  );

  if (variant === 'temporary') {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
      >
        {drawer}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
      }}
    >
      {drawer}
    </Drawer>
  );
}
